import type { ReactNode } from 'react'
import { Link, NavLink } from 'react-router-dom'
import { useSessionStore } from '@/features/auth/sessionStore'
import { CartIcon } from '@/components/ui/icons'
import { UserMenu } from './UserMenu'
import s from './site.module.css'

/* The one header, on every screen except the exam.

   It has four states, driven by who is signed in: a visitor is invited to
   log in or register, a student gets their test series and an account menu,
   an admin gets the portal badge and an account menu.

   A share-link GUEST is the fourth, and is treated as a visitor rather than as
   a signed-in user — which is the correct reading of a guest token. It opens
   one exam and its outcome and nothing else, so there is no account menu to
   offer and no dashboard to link to; what a guest wants from this bar is the
   brand, and the invitation to register that turns their attempt into an
   account. The cart is the one thing withheld: a guest reached us through
   somebody's link, not through the store, and a basket they cannot fill is
   noise on the page where they have just finished writing. */

export function SiteHeader(): ReactNode {
  const status = useSessionStore((state) => state.status)
  const userType = useSessionStore((state) => state.user?.user_type)

  const isStudent = status === 'authenticated' && userType === 'student'
  const isAdmin = status === 'authenticated' && userType === 'admin'
  const isGuest = status === 'authenticated' && userType === 'guest'
  /* A guest falls in here deliberately: no UserMenu, because every entry in it
     — profile, my test series, sign out — is a route a guest token cannot
     open. They get Login and Register, which are public and both work. */
  const isAnonymous = !isStudent && !isAdmin

  return (
    <header className={s.header}>
      <div className={s.headerInner}>
        {/* An admin's home is the portal, not the marketing site. */}
        <Link to={isAdmin ? '/admin' : '/'} className={s.brand}>
          <img src="/favicon.svg" alt="" className={s.brandMark} />
          <span className={s.brandName}>Shantha Marga</span>
        </Link>
        {isAdmin && <span className={s.brandTag}>Admin</span>}

        <div className={s.spacer} />

        <nav className={s.nav} aria-label="Main">
          {/* The cart is a store affordance — it has no meaning inside the
              admin portal, nor to a guest who arrived on a share link. */}
          {!isAdmin && !isGuest && (
            <Link to="/cart" className={s.navLink} aria-label="Cart">
              <CartIcon />
              <span className={s.navLinkLabel}>Cart</span>
            </Link>
          )}

          {isStudent && (
            <NavLink
              to="/dashboard"
              className={({ isActive }) =>
                [s.navLink, isActive ? s.navLinkActive : ''].filter(Boolean).join(' ')
              }
            >
              My test series
            </NavLink>
          )}

          {isAnonymous ? (
            <>
              <Link to="/login" className={s.navLink}>
                Login
              </Link>
              <Link to="/signup" className={s.navCta}>
                Register
              </Link>
            </>
          ) : (
            <UserMenu />
          )}
        </nav>
      </div>
    </header>
  )
}
